/**********************************************************************************************************
 *   SHARED IMPORTS
 **********************************************************************************************************/
import { useRef, useState } from 'react'
import { Field, Form, Formik, FormikProps } from 'formik'
import styled from 'styled-components'
import * as Yup from 'yup'

/**********************************************************************************************************
 *   SHARED IMPORTS
 **********************************************************************************************************/
import { Form as NXUIForm, Lightbox } from '@nexigendigital/nxui'
import { useEndUserSessionsMutation } from 'api/account'
import { Timer } from 'helpers/utils'

/**********************************************************************************************************
 *   INTERFACE
 **********************************************************************************************************/
interface Props {
    status: boolean,
    close: () => void
}

const PasswordLightbox = {
    Form: styled.div`
        max-width: 310px;
        width: 100%;
        margin-top: 1rem;
        text-align: left;
    `
}

/**********************************************************************************************************
 *   COMPONENT START
 **********************************************************************************************************/
export default function EndActiveSessionLightbox({ status, close }: Props) {
    const [showForm, setShowForm] = useState(false)
    const formRef = useRef<FormikProps<{ password: string, confirm_password: string }>>(null)
    const [endUserSessions, { isSuccess }] = useEndUserSessionsMutation()

    function renderLightbox() {
        if (isSuccess) {
            return {
                type: 'success',
                title: 'Active Sessions Ended',
                description: 'All other sessions have been ended.',
                actions: [{
                    label: 'Continue',
                    color: 'primary',
                    func: () => {
                        close()
                        Timer(() => {
                            setShowForm(false)
                        }, 250)
                    }
                }]
            }
        }

        if (showForm) {
            const renderForm = () => {
                return (
                    <Formik
                        innerRef={formRef}
                        initialValues={{
                            password: '',
                            confirm_password: ''
                        }}
                        validationSchema={
                            Yup.object().shape({
                                password: Yup.string()
                                    .required('Required'),
                                confirm_password: Yup.string()
                                    .oneOf([Yup.ref('password'), null], 'Password must match')
                                    .required('Required')
                            })
                        }
                        onSubmit={({ password }) => {
                            endUserSessions({ password })
                        }}
                    >
                        {({ isSubmitting }) => {
                            return (
                                <Form id={'endActiveSessionForm'}>
                                    <Field
                                        id={'password'}
                                        label={'Password'}
                                        name={'password'}
                                        type={'password'}
                                        autoComplete={'current-password'}
                                        disabled={isSubmitting}
                                        component={NXUIForm.InputField}
                                    />
                                    <Field
                                        id={'confirm_password'}
                                        label={'Confirm Password'}
                                        name={'confirm_password'}
                                        type={'password'}
                                        autoComplete={'confirm-password'}
                                        disabled={isSubmitting}
                                        component={NXUIForm.InputField}
                                    />
                                </Form>
                            )
                        }}
                    </Formik>
                )
            }

            /*   RENDER COMPONENT
            **********************************************************************************************************/
            return {
                type: 'confirm',
                title: 'End Active Sessions',
                description:
                    <>
                        <p>Confirm your password to end all active sessions.</p>
                        <PasswordLightbox.Form>
                            {renderForm()}
                        </PasswordLightbox.Form>
                    </>,
                actions: [
                    {
                        label: 'Back',
                        color: 'secondary',
                        func: () => {
                            close()
                            Timer(() => {
                                setShowForm(false)
                            }, 250)
                        }
                    }, {
                        label: 'End Active Sessions',
                        color: 'primary',
                        type: 'submit',
                        form: 'endActiveSessionForm',
                        loading: formRef?.current?.isSubmitting ? 'Ending Sessions...' : false,
                        func: () => formRef?.current?.handleSubmit()
                    }
                ]
            }
        }

        return {
            type: 'confirm',
            title: 'End Active Sessions',
            description: 'Doing this will end all sessions, except for your current one. Only your current session will remain logged in.',
            actions: [
                {
                    label: 'Back',
                    color: 'secondary',
                    func: () => {
                        close()
                        Timer(() => {
                            setShowForm(false)
                        }, 250)
                    }
                }, {
                    label: 'End Active Sessions',
                    color: 'primary',
                    func: () => setShowForm(true)
                }
            ]
        }
    }

    /*   RENDER COMPONENT
    **********************************************************************************************************/
    return (
        <Lightbox
            {...renderLightbox()}
            conditions={{ state: status, action: () => close() }}
        />
    )
}
